import React, { useState, useEffect, useRef } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { BaseFieldProps } from './types';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Card, CardContent } from "@/shared/components/ui/card";
import { PlusCircle, Trash2 } from "lucide-react";

interface StructuredFilterFieldProps {
  field: BaseFieldProps['field'];
  name: string;
  control: Control<Record<string, unknown>>;
  formValues?: Record<string, unknown>; // Current form values for visibility evaluation
  renderField?: (key: string, field: BaseFieldProps['field']) => React.ReactNode;
  renderArrayField?: (key: string, field: BaseFieldProps['field']) => React.ReactNode;
}

interface WebhookFilter {
  path: string;
  operation: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan' | 'exists' | 'notExists';
  value?: string | number | boolean;
  dataType?: 'string' | 'number' | 'boolean' | 'date' | 'array';
}

interface ArrayWebhookFilter {
  arrayPath: string;
  arrayOperation: 'some' | 'every' | 'none';
  path?: string;
  operation?: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan' | 'exists' | 'notExists';
  value?: string | number | boolean;
  dataType?: 'string' | 'number' | 'boolean' | 'date' | 'array';
  filters?: WebhookFilter[];
}

type FilterType = WebhookFilter | ArrayWebhookFilter;


const StructuredFilterField: React.FC<StructuredFilterFieldProps> = ({
  field,
  name,
  control,
}) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();

  // Traduções dinâmicas para operações
  const OPERATIONS = [
    { value: 'equals', label: t('workflow.webhook.filter.operations.equals') },
    { value: 'contains', label: t('workflow.webhook.filter.operations.contains') },
    { value: 'startsWith', label: t('workflow.webhook.filter.operations.startsWith') },
    { value: 'endsWith', label: t('workflow.webhook.filter.operations.endsWith') },
    { value: 'exists', label: t('workflow.webhook.filter.operations.exists') },
    { value: 'notExists', label: t('workflow.webhook.filter.operations.notExists') },
    { value: 'greaterThan', label: t('workflow.webhook.filter.operations.greaterThan') },
    { value: 'lessThan', label: t('workflow.webhook.filter.operations.lessThan') }
  ];

  const ARRAY_OPERATIONS = [
    { value: 'some', label: t('workflow.webhook.filter.arrayOperations.some') },
    { value: 'every', label: t('workflow.webhook.filter.arrayOperations.every') },
    { value: 'none', label: t('workflow.webhook.filter.arrayOperations.none') }
  ];

  
  const [filters, setFilters] = useState<FilterType[]>([]);

  // Inicializar com valores existentes - executar apenas uma vez na montagem do componente
  useEffect(() => {
    const currentValue = watch(name);
    
    // Valor padrão caso nenhuma condição seja atendida
    let filtersToSet: FilterType[] = [];
    let shouldUpdateFormValue = true;
    
    // Se houver valor, tentar converter para array de filtros
    if (currentValue) {
        // Solução simplificada: converter diretamente para array se for um objeto array-like
        if (currentValue && typeof currentValue === 'object') {
          
          // Se já estiver no formato esperado {type: 'array', value: [...]}
          if ('type' in currentValue && currentValue.type === 'array' && 'value' in currentValue) {
            if (Array.isArray(currentValue.value)) {
              filtersToSet = currentValue.value;
              // Já está no formato correto, não precisa atualizar o form value
              shouldUpdateFormValue = false;
            }
          }
          // Se for um array-like object (tem length e índices numéricos)
          else if ('length' in currentValue && typeof currentValue.length === 'number') {
            
            // Converter para array e garantir que seja do tipo FilterType[]
            const convertedArray = Array.from(currentValue) as unknown as FilterType[];
            
            filtersToSet = convertedArray;
          }
          // Se for um único objeto de filtro, envolver em array
          else if (('path' in currentValue && 'operation' in currentValue) || 
              ('arrayPath' in currentValue && 'arrayOperation' in currentValue && 'filters' in currentValue)) {
            filtersToSet = [currentValue as FilterType];
          }
        }
        // Se for uma string, tentar fazer parse para JSON
        else if (typeof currentValue === 'string') {
            const parsedValue = JSON.parse(currentValue);
            filtersToSet = Array.isArray(parsedValue) ? parsedValue : [parsedValue];

        }

    }
    
    // Atualizar o estado local com os filtros
    setFilters(filtersToSet);
    
    // Atualizar o valor do formulário se necessário
    if (shouldUpdateFormValue) {
      setValue(name, { type: 'array', value: filtersToSet }, { shouldValidate: false });
    }
  // Removido watch da lista de dependências para evitar re-renderizações desnecessárias
  // Esta inicialização deve ocorrer apenas uma vez
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, setValue]);

  // Atualizar o form quando os filtros mudam
  // Usamos useRef para rastrear a última versão dos filtros que já foi sincronizada
  const lastSyncedFiltersRef = useRef<FilterType[]>([]);
  
  useEffect(() => {
    
    // Verificar se os filtros realmente mudaram para evitar atualizações desnecessárias
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(lastSyncedFiltersRef.current);
    
    if (filtersChanged) {
      // Sempre usar o formato correto {type: 'array', value: [...]} para o valor do formulário
      const updatedValue = { type: 'array', value: filters };
      
      // Usar shouldDirty: true para garantir que o formulário detecte a mudança
      setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
      
      // Atualizar a referência dos últimos filtros sincronizados
      lastSyncedFiltersRef.current = [...filters];
    }
  }, [filters, name, setValue]);

  // Adicionar um novo filtro simples
  const addSimpleFilter = () => {
    const newFilter: WebhookFilter = {
      path: '',
      operation: 'equals',
      value: ''
    };
    const newFilters = [...filters, newFilter];
    setFilters(newFilters);
    
    // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
    const updatedValue = { type: 'array', value: newFilters };
    setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
  };

  // Adicionar um novo filtro de array
  const addArrayFilter = () => {
    const newFilter: ArrayWebhookFilter = {
      arrayPath: '',
      arrayOperation: 'some',
      filters: [{
        path: '',
        operation: 'equals',
        value: ''
      }]
    };
    const newFilters = [...filters, newFilter];
    setFilters(newFilters);
    
    // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
    const updatedValue = { type: 'array', value: newFilters };
    setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
  };

  // Remover um filtro
  const removeFilter = (index: number) => {
    const newFilters = [...filters];
    newFilters.splice(index, 1);
    setFilters(newFilters);
    
    // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
    const updatedValue = { type: 'array', value: newFilters };
    setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
  };

  // Atualizar um filtro simples
  const updateSimpleFilter = (index: number, field: keyof WebhookFilter, value: WebhookFilter[keyof WebhookFilter]) => {
    const newFilters = [...filters];
    const filter = newFilters[index] as WebhookFilter;

    if ('path' in filter) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (filter as Record<string, any>)[field] = value;

      // Se a operação for exists ou notExists, remover o campo value
      if (field === 'operation' && (value === 'exists' || value === 'notExists')) {
        delete (filter as Partial<WebhookFilter>).value;
      } else if (field === 'operation' && filter.operation !== 'exists' && filter.operation !== 'notExists' && filter.value === undefined) {
        filter.value = '';
      }
      
      setFilters(newFilters);
      
      // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
      const updatedValue = { type: 'array', value: newFilters };
      setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
    }
  };

  // Atualizar um filtro de array
  const updateArrayFilter = (index: number, field: keyof ArrayWebhookFilter, value: ArrayWebhookFilter[keyof ArrayWebhookFilter]) => {
    const newFilters = [...filters];
    const filter = newFilters[index] as ArrayWebhookFilter;

    if ('arrayPath' in filter) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (filter as Record<string, any>)[field] = value;
      setFilters(newFilters);
      
      // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
      const updatedValue = { type: 'array', value: newFilters };
      setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
    }
  };

  // Adicionar um filtro interno a um filtro de array
  const addInnerFilter = (arrayFilterIndex: number) => {
    const newFilters = [...filters];
    const arrayFilter = newFilters[arrayFilterIndex] as ArrayWebhookFilter;
    
    if ('filters' in arrayFilter) {
      if (!arrayFilter.filters) {
        arrayFilter.filters = [];
      }
      arrayFilter.filters.push({
        path: '',
        operation: 'equals',
        value: ''
      });
      setFilters(newFilters);
      
      // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
      const updatedValue = { type: 'array', value: newFilters };
      setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
    }
  };

  // Remover um filtro interno de um filtro de array
  const removeInnerFilter = (arrayFilterIndex: number, innerIndex: number) => {
    const newFilters = [...filters];
    const arrayFilter = newFilters[arrayFilterIndex] as ArrayWebhookFilter;
    
    if ('filters' in arrayFilter && arrayFilter.filters) {
      arrayFilter.filters.splice(innerIndex, 1);
      setFilters(newFilters);
      
      // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
      const updatedValue = { type: 'array', value: newFilters };
      setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
    }
  };

  // Atualizar um filtro interno de um filtro de array
  const updateInnerFilter = (arrayFilterIndex: number, innerIndex: number, field: keyof WebhookFilter, value: WebhookFilter[keyof WebhookFilter]) => {
    const newFilters = [...filters];
    const arrayFilter = newFilters[arrayFilterIndex] as ArrayWebhookFilter;

    if ('filters' in arrayFilter && arrayFilter.filters) {
      const innerFilter = arrayFilter.filters[innerIndex];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (innerFilter as Record<string, any>)[field] = value;

      // Se a operação for exists ou notExists, remover o campo value
      if (field === 'operation' && (value === 'exists' || value === 'notExists')) {
        delete (innerFilter as Partial<WebhookFilter>).value;
      } else if (field === 'operation' && innerFilter.operation !== 'exists' && innerFilter.operation !== 'notExists' && innerFilter.value === undefined) {
        innerFilter.value = '';
      }
      
      setFilters(newFilters);
      
      // Garantir que o valor do formulário seja atualizado imediatamente com o formato correto
      const updatedValue = { type: 'array', value: newFilters };
      setValue(name, updatedValue, { shouldValidate: true, shouldDirty: true });
    }
  };

  // Renderizar um filtro simples
  const renderSimpleFilter = (filter: WebhookFilter, index: number) => {
    
    // Verificar se o filtro tem a estrutura esperada
    if (!filter || typeof filter !== 'object') {
      return null;
    }
    
    if (!('path' in filter) || !('operation' in filter)) {
      return null;
    }
    
    const needsValue = filter.operation !== 'exists' && filter.operation !== 'notExists';
    
    return (
      <div key={index} className="p-3 border rounded-md mb-3 bg-background">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-medium">{t('workflow.webhook.filter.filterTitle', { index: index + 1 })}</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeFilter(index)}
            className="h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-medium mb-1 block">{t('workflow.webhook.filter.path')}</label>
            <Input
              placeholder="data.field"
              value={filter.path}
              onChange={(e) => updateSimpleFilter(index, 'path', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">{t('workflow.webhook.filter.operation')}</label>
            <Select
              value={filter.operation}
              onValueChange={(value) => updateSimpleFilter(index, 'operation', value as WebhookFilter['operation'])}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPERATIONS.map((op) => (
                  <SelectItem key={op.value} value={op.value}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {needsValue && (
            <div>
              <label className="text-sm font-medium mb-1 block">{t('workflow.webhook.filter.value')}</label>
              <Input
                placeholder={t('workflow.webhook.filter.valuePlaceholder')}
                value={typeof filter.value === 'string' || typeof filter.value === 'number' ? filter.value : ''}
                onChange={(e) => updateSimpleFilter(index, 'value', e.target.value)}
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Renderizar um filtro de array
  const renderArrayFilter = (filter: ArrayWebhookFilter, index: number) => {
    
    // Verificar se o filtro tem a estrutura esperada
    if (!filter || typeof filter !== 'object') {
      return null;
    }
    
    if (!('arrayPath' in filter) || !('arrayOperation' in filter) || !('filters' in filter)) {
      return null;
    }
    
    return (
      <div key={index} className="p-3 border rounded-md mb-3 bg-background">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-medium">{t('workflow.webhook.filter.arrayFilterTitle', { index: index + 1 })}</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeFilter(index)}
            className="h-8 w-8 p-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-sm font-medium mb-1 block">{t('workflow.webhook.filter.arrayPath')}</label>
            <Input
              placeholder="data.items"
              value={filter.arrayPath}
              onChange={(e) => updateArrayFilter(index, 'arrayPath', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">{t('workflow.webhook.filter.arrayOperation')}</label>
            <Select
              value={filter.arrayOperation}
              onValueChange={(value) => updateArrayFilter(index, 'arrayOperation', value as ArrayWebhookFilter['arrayOperation'])}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ARRAY_OPERATIONS.map((op) => (
                  <SelectItem key={op.value} value={op.value}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="mt-3 border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h5 className="text-sm font-medium">Condições do Item</h5>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addInnerFilter(index)}
              className="h-7"
            >
              <PlusCircle className="h-3.5 w-3.5 mr-1" />
              {t('workflow.webhook.filter.conditions.add')}
            </Button>
          </div>
          
          {(!filter.filters || filter.filters.length === 0) ? (
            <div className="text-sm text-muted-foreground p-2 text-center">
              Nenhuma condição definida
            </div>
          ) : (
            filter.filters?.map((innerFilter, innerIndex) => {
              const needsValue = innerFilter.operation !== 'exists' && innerFilter.operation !== 'notExists';
              
              return (
                <div key={innerIndex} className="p-2 border rounded-md mb-2 bg-muted/30">
                  <div className="flex justify-between items-center mb-2">
                    <h6 className="text-xs font-medium">{t('workflow.webhook.filter.conditionTitle', { index: innerIndex + 1 })}</h6>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeInnerFilter(index, innerIndex)}
                      className="h-6 w-6 p-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <Input
                        placeholder={t('workflow.webhook.filter.arrayPathPlaceholder')}
                        value={innerFilter.path}
                        onChange={(e) => updateInnerFilter(index, innerIndex, 'path', e.target.value)}
                        className="w-full text-sm"
                      />
                    </div>
                    
                    <div>
                      <Select
                        value={innerFilter.operation}
                        onValueChange={(value) => updateInnerFilter(index, innerIndex, 'operation', value as WebhookFilter['operation'])}
                      >
                        <SelectTrigger className="w-full text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATIONS.map((op) => (
                            <SelectItem key={op.value} value={op.value}>
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {needsValue && (
                      <div>
                        <Input
                          placeholder={t('workflow.webhook.filter.valuePlaceholder')}
                          value={typeof innerFilter.value === 'string' || typeof innerFilter.value === 'number' ? innerFilter.value : ''}
                          onChange={(e) => updateInnerFilter(index, innerIndex, 'value', e.target.value)}
                          className="w-full text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // Renderizar o construtor visual
  const renderVisualBuilder = () => {
    return (
    <div className="space-y-4">
      {(!filters || filters.length === 0) ? (
        <div className="text-center p-6 border border-dashed rounded-md">
          <p className="text-muted-foreground mb-4">{t('workflow.webhook.filter.description')}</p>
          <div className="flex justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={addSimpleFilter}
              className="flex items-center gap-2"
            >
              <PlusCircle className="h-4 w-4" />
              {t('workflow.webhook.filter.addFilter')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={addArrayFilter}
              className="flex items-center gap-2"
            >
              <PlusCircle className="h-4 w-4" />
              {t('workflow.webhook.filter.addArrayFilter')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {filters.map((filter, index) => {
            
            if (!filter) {
              return null;
            }
            
            if (typeof filter !== 'object') {
              return null;
            }
            
            if ('arrayPath' in filter) {
              return renderArrayFilter(filter as ArrayWebhookFilter, index);
            } else {
              return renderSimpleFilter(filter as WebhookFilter, index);
            }
          })}
          
          <div className="flex gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addSimpleFilter}
              className="flex items-center gap-2"
            >
              <PlusCircle className="h-4 w-4" />
              {t('workflow.webhook.filter.addFilter')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addArrayFilter}
              className="flex items-center gap-2"
            >
              <PlusCircle className="h-4 w-4" />
              {t('workflow.webhook.filter.addArrayFilter')}
            </Button>
          </div>
        </>
      )}
      
    </div>
  );
  }

  
  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem>
          <FormLabel>{field.label}</FormLabel>
          <FormControl>
            <Card>
              <CardContent className="pt-4">
                <div className="mb-4 border-b pb-4">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('workflow.webhook.filter.cardTitle')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('workflow.webhook.filter.cardDescription')}
                  </p>
                </div>
                {renderVisualBuilder()}
              </CardContent>
            </Card>
          </FormControl>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default StructuredFilterField;
